import React from 'react';
import { Circle, Ellipse, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Duct area — both rectangular (W×H) and round (D) options shown side-by-side. */
export function DiagramDuctArea() {
  // Rectangular cross-section (left)
  const rx = 90, ry = 105, rw = 100, rh = 80;
  const rcx = rx + rw / 2;
  const rcy = ry + rh / 2;

  // Round cross-section (right)
  const cx = 265, cy = 145, R = 42;

  return (
    <DiagramCanvas>
      {/* === Rectangular === */}
      <Rect x={rx} y={ry} width={rw} height={rh} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {/* Hatch shading to indicate cross-section area */}
      {Array.from({ length: 8 }).map((_, i) => {
        const t = (i + 1) / 9;
        return (
          <Line key={i} x1={rx + 4} y1={ry + t * rh} x2={rx + rw - 4} y2={ry + t * rh}
            stroke={DT.varColor} strokeWidth="0.5" opacity={0.35} />
        );
      })}

      {/* W dimension (top) */}
      <Line x1={rx} y1={ry - 16} x2={rx + rw} y2={ry - 16} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx} y1={ry - 20} x2={rx} y2={ry - 12} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx + rw} y1={ry - 20} x2={rx + rw} y2={ry - 12} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={rcx} y={ry - 20} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">W (in)</SvgText>

      {/* H dimension (left) */}
      <Line x1={rx - 16} y1={ry} x2={rx - 16} y2={ry + rh} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx - 20} y1={ry} x2={rx - 12} y2={ry} stroke={DT.dim} strokeWidth="1" />
      <Line x1={rx - 20} y1={ry + rh} x2={rx - 12} y2={ry + rh} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={rx - 22} y={rcy + 3} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="end">H</SvgText>

      <SvgText x={rcx} y={ry + rh + 16} fill={DT.desc} fontSize="9" fontFamily={DT.mono} textAnchor="middle">Rectangular</SvgText>

      {/* === Round === */}
      <Ellipse cx={cx} cy={cy} rx={R} ry={R} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />
      {/* Concentric hatching */}
      {[0.3, 0.55, 0.8].map((f, i) => (
        <Circle key={i} cx={cx} cy={cy} r={R * f} fill="none"
          stroke={DT.varColor} strokeWidth="0.5" opacity={0.35} />
      ))}
      {/* Diameter line */}
      <Line x1={cx - R} y1={cy} x2={cx + R} y2={cy} stroke={DT.dim} strokeWidth="1" strokeDasharray="3,3" />
      <Circle cx={cx - R} cy={cy} r={1.5} fill={DT.dim} />
      <Circle cx={cx + R} cy={cy} r={1.5} fill={DT.dim} />
      <SvgText x={cx} y={cy + R + 16} fill={DT.desc} fontSize="9" fontFamily={DT.mono} textAnchor="middle">Round</SvgText>

      {/* OR divider */}
      <Line x1={208} y1={108} x2={208} y2={188} stroke={DT.dim} strokeWidth="0.7" strokeDasharray="2,3" />
      <SvgText x={208} y={100} fill={DT.desc} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="600">OR</SvgText>

      {/* Labels */}
      <LeaderLine x1={rcx} y1={rcy} x2={50} y2={70} varName="A" desc="W·H/144 (ft²)" anchor="start" />
      <LeaderLine x1={cx} y1={cy} x2={cx + 50} y2={70} varName="A" desc="πD²/(4·144) (ft²)" anchor="end" />
      <LeaderLine x1={cx + R * 0.7} y1={cy - R * 0.7} x2={cx + 60} y2={210} varName="D" desc="Diameter (in)" anchor="end" />

      <FormulaTag formula="A_rect = W·H/144   A_round = π·D²/(4·144)  ft²" />
    </DiagramCanvas>
  );
}
